import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { flowService } from '../services/flowService';

/**
 * Whether a flow's live data has somewhere to go: FlowGate answers a booking flow itself, and any
 * other flow needs a server connected in its Connections. Validation asks this about every step
 * that exchanges data. `endpointUri` cannot answer it, because it is only set once the flow is
 * published, so an unpublished booking flow would read as having nowhere to get its times from.
 *
 * One hook for both builders, so the canvas and the assistant can never disagree about it.
 */
export function useLiveDataConnected(flowId) {
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const envId = selectedEnvironment?.id;
  const [connected, setConnected] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!flowId || !envId || !flowService.getLiveData) {
      setConnected(false);
      return undefined;
    }
    let cancelled = false;
    Promise.resolve(flowService.getLiveData(envId, flowId, authenticatedFetch))
      .then((live) => {
        if (!cancelled) setConnected(!!live && (live.bookingFlow || live.mode !== 'NONE'));
      })
      .catch(() => { if (!cancelled) setConnected(false); });
    return () => { cancelled = true; };
    // authenticatedFetch is recreated every AuthContext render; depending on it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId, envId, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  return { connected, refresh };
}
